import { Button, Card, Divider, RadixTable, TColumn } from '@features/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TTradingPlatform, TTradingPlatformQuery } from '@src/types';
import { CheckIcon } from 'lucide-react';
import Pagination from '@features/ui/components/pagination.tsx';
import { useNavigate } from 'react-router-dom';
import { getTradingPlatforms } from '@src/services/tradingPlatforms.ts';
import { CancelIcon } from '@features/ui/icons';

export const TradingPlatforms = () => {
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [tradingPlatforms, setTradingPlatforms] = useState<TTradingPlatform[]>(
    []
  );

  const navigate = useNavigate();

  const fetchTradingPlatforms = useCallback(
    async (params?: TTradingPlatformQuery) => {
      const res = await getTradingPlatforms(params);

      setTotalPage(res.data.total_pages);

      const platformsSorted = res.data.platforms.sort((a, b) => {
        const aId = Number(a?.id);
        const bId = Number(b?.id);

        if (aId > bId) {
          return 1;
        }
        if (aId < bId) {
          return -1;
        }

        return 0;
      });

      setTradingPlatforms(platformsSorted);
    },
    []
  );

  useEffect(() => {
    fetchTradingPlatforms().then();
  }, [fetchTradingPlatforms]);

  const handleEditPlatform = useCallback(
    (id?: number) => {
      if (!id) return;
      navigate(`/admin/trading-platforms/${id}`);
    },
    [navigate]
  );

  const handleChangePage = useCallback(
    async (page: number) => {
      setPage(page);
      await fetchTradingPlatforms({ page });
    },
    [fetchTradingPlatforms]
  );

  const columns = useMemo(
    (): TColumn<TTradingPlatform>[] => [
      {
        title: 'Name',
        render: (item) => <p>{item?.name}</p>,
      },
      {
        title: (
          <div className={'flex justify-center'}>
            <p>Status</p>
          </div>
        ),
        render: (item) => (
          <div className={'flex justify-center'}>
            {item?.is_publish ? (
              <div
                className={
                  'bg-lime h-4.5 w-4.5 rounded-full flex items-center justify-center'
                }
              >
                <CheckIcon />
              </div>
            ) : (
              <CancelIcon />
            )}
          </div>
        ),
      },
      {
        title: (
          <div className={'flex justify-center'}>
            <p>Action</p>
          </div>
        ),
        render: (item) => (
          <div className={'flex justify-center gap-2'}>
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={() => handleEditPlatform(item?.id)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [handleEditPlatform]
  );

  return (
    <div>
      <div
        className={
          'flex lg:flex-row flex-col justify-between lg:items-center gap-2 lg:mb-4.5 mb-2'
        }
      >
        <h1 className={'text-[23px]'}>Trading Platforms</h1>
        <div className={'flex lg:flex-row flex-col gap-[56px]'}>
          <Button
            className={'w-fit'}
            size={'sm'}
            variant={'lime'}
            onClick={() => navigate('/admin/trading-platforms/create')}
          >
            Create New Trading Platform
          </Button>
        </div>
      </div>
      <Card className={'items-end'}>
        <RadixTable columns={columns} data={tradingPlatforms} />
        <Divider />
        <Pagination
          className={'mt-6'}
          current={page}
          total={totalPage}
          onChange={handleChangePage}
        />
      </Card>
    </div>
  );
};
